import React, { useMemo } from 'react';
import { Html } from '@react-three/drei';
import { ROOM_COLORS, interiorWalls, wingsOf, inWings, defaultFacade } from './model3d';
import { Box, Mat, WingShell, ORANGE } from './Facade';
import { WingRoof } from './Roofs';

const Label = ({ position, children, accent }) => (
  <Html position={position} center zIndexRange={[20, 0]} style={{ pointerEvents: 'none' }}>
    <div className={`whitespace-nowrap px-1.5 py-0.5 text-[10px] font-semibold shadow ${accent ? 'bg-[#F26B1F] text-white' : 'bg-black/75 text-white'}`}>{children}</div>
  </Html>
);

const Interior = ({ fl, i, model, wings, show, hl, clip, selectedRoom, onSelectRoom, markMode, showLabels }) => {
  const { width: W, depth: D } = model.footprint;
  const H = model.floor_height;
  const base = i * H;
  const rooms = useMemo(() => fl.rooms.map((r, ri) => ({ ...r, ri })).filter((r) => inWings(wings, r.x + r.w / 2, r.y + r.d / 2, i + 1)), [fl.rooms, wings, i]);
  const walls = useMemo(() => interiorWalls(rooms, W, D), [rooms, W, D]);
  const labelSet = useMemo(() => {
    const picked = [];
    const minGap = Math.max(W, D) * 0.18;
    [...rooms].sort((a, b) => b.w * b.d - a.w * a.d).forEach((r) => {
      const c = [r.x + r.w / 2, r.y + r.d / 2];
      if (picked.length < 6 && picked.every((p) => Math.hypot(p.c[0] - c[0], p.c[1] - c[1]) > minGap)) picked.push({ ri: r.ri, c });
    });
    return new Set(picked.map((p) => p.ri));
  }, [rooms, W, D]);
  return (
    <group>
      {show.intWalls && walls.map((w, wi) => (
        <Box key={wi} args={w.orient === 'h' ? [w.e - w.s, H - 0.12, 0.12] : [0.12, H - 0.12, w.e - w.s]}
          position={w.orient === 'h' ? [(w.s + w.e) / 2, base + (H - 0.12) / 2, w.c] : [w.c, base + (H - 0.12) / 2, (w.s + w.e) / 2]}>
          <Mat color="#EFEAE3" hl={hl === 'intWalls'} clip={clip} />
        </Box>
      ))}
      {show.rooms && rooms.map((r) => {
        const sel = selectedRoom && selectedRoom.level === fl.level && selectedRoom.idx === r.ri;
        return (
          <group key={r.ri}>
            <Box args={[Math.max(r.w - 0.14, 0.2), 0.05, Math.max(r.d - 0.14, 0.2)]} position={[r.x + r.w / 2, base + 0.03, r.y + r.d / 2]} shadow={false}
              onClick={markMode ? (e) => { e.stopPropagation(); onSelectRoom({ ...r, level: fl.level, floorName: fl.name, idx: r.ri }); } : undefined}
              onPointerOver={markMode ? () => { document.body.style.cursor = 'pointer'; } : undefined}
              onPointerOut={markMode ? () => { document.body.style.cursor = ''; } : undefined}>
              <Mat color={sel ? ORANGE : ROOM_COLORS[r.type] || ROOM_COLORS.other} hl={hl === 'rooms'} clip={clip} />
            </Box>
            {((showLabels && labelSet.has(r.ri)) || sel) && <Label position={[r.x + r.w / 2, base + 0.6, r.y + r.d / 2]} accent={sel}>{r.name} · {Math.round(r.w * r.d)} m²</Label>}
          </group>
        );
      })}
    </group>
  );
};

export const Building = ({ model, show, highlight, clip, floorLimit, selectedRoom, onSelectRoom, markMode, showDims }) => {
  const { width: W, depth: D } = model.footprint;
  const H = model.floor_height;
  const wings = useMemo(() => wingsOf(model), [model]);
  const facade = defaultFacade(model);
  const spacing = model.window_spacing || 3.2;
  const visible = model.floors.slice(0, floorLimit);
  const top = visible.length * H;
  const main = wings.reduce((a, b) => (b.w * b.floors > a.w * a.floors ? b : a), wings[0]);
  return (
    <group position={[-W / 2, 0, -D / 2]}>
      {wings.map((wg, wi) => {
        const fv = Math.min(wg.floors, floorLimit);
        return (
          <group key={wi}>
            {show.foundation && <Box args={[wg.w + 0.5, 0.6, wg.d + 0.5]} position={[wg.x + wg.w / 2, -0.3, wg.y + wg.d / 2]}><Mat color="#6f6b66" hl={highlight === 'foundation'} clip={clip} /></Box>}
            {show.slabs && Array.from({ length: Math.max(fv - 1, 0) }, (_, k) => (
              <Box key={k} args={[wg.w, 0.28, wg.d]} position={[wg.x + wg.w / 2, (k + 1) * H - 0.14, wg.y + wg.d / 2]} shadow={false}>
                <Mat color="#A7A29A" hl={highlight === 'slabs'} clip={clip} />
              </Box>
            ))}
            <WingShell wing={wg} wings={wings} H={H} fv={fv} facade={facade} spacing={spacing} show={show} hl={highlight} clip={clip}
              entrance={wg === main ? wg.x + wg.w / 2 : null} />
            {show.roof && fv === wg.floors && <WingRoof wing={wg} top={fv * H} facade={facade} hl={highlight === 'roof'} clip={clip} />}
          </group>
        );
      })}
      {visible.map((fl, i) => (
        <Interior key={fl.level} fl={fl} i={i} model={model} wings={wings} show={show} hl={highlight} clip={clip} selectedRoom={selectedRoom}
          onSelectRoom={onSelectRoom} markMode={markMode} showLabels={showDims && i === visible.length - 1} />
      ))}
      {showDims && (
        <group>
          <Label position={[W / 2, 0.2, -2.2]} accent>{W.toFixed(1)} m</Label>
          <Label position={[-2.2, 0.2, D / 2]} accent>{D.toFixed(1)} m</Label>
          <Label position={[W + 1.8, top / 2, 0]} accent>{top.toFixed(1)} m</Label>
        </group>
      )}
    </group>
  );
};
